import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box, Typography, Paper, Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

const connectionURL = process.env.REACT_APP_API_URL;;

const Timeline = () => {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [entries, setEntries] = useState([]);
  const [openImage, setOpenImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  // Fetch available years
  const fetchYears = async () => {
    try {
      const response = await axios.get(`${connectionURL}/api/dates/years`);
      setYears(response.data);
      if (response.data.length > 0) {
        setSelectedYear(response.data[0]);  // Set to oldest year
      }
    } catch (error) {
      console.error('Error fetching years:', error);
    }
  };

  // Fetch entries for the selected year
  const fetchEntries = async (year) => {
    try {
      const response = await axios.get(`${connectionURL}/api/dates/entries?year=${year}`);
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  };

  // Fetch years on component mount
  useEffect(() => {
    fetchYears();
  }, []);

  // Fetch entries when selectedYear changes
  useEffect(() => {
    if (selectedYear) {
      fetchEntries(selectedYear);
    }
  }, [selectedYear]);

  // Handle year tab change
  const handleYearChange = (event, newValue) => {
    setSelectedYear(newValue);
  };

  // Handle image click
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenImage(true);
  };

  // Handle modal close
  const handleCloseImage = () => {
    setOpenImage(false);
    setSelectedImage('');
  };

  // Filter and sort entries by ascending date within the selected year
  const filteredEntries = entries
    .sort((a, b) => new Date(a.date) - new Date(b.date));  // Ascending order

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Sticky Tabs */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}>
        <Tabs
          value={selectedYear}
          onChange={handleYearChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Year Tabs"
        >
          {years.map((year) => (
            <Tab key={year} label={year} value={year} />
          ))}
        </Tabs>
      </Box>

      {/* Timeline Entries */}
      <Box sx={{ p: 2 }}>
        {filteredEntries.length > 0 ? (
          filteredEntries.map((entry) => (
            <Paper key={entry._id} elevation={3} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">{entry.location}</Typography>
              <Typography variant="body2">
                {new Date(entry.date).toISOString().substring(5, 7)}/
                {new Date(entry.date).toISOString().substring(8, 10)}/
                {new Date(entry.date).getUTCFullYear()}
              </Typography>
              <img
                src={entry.imageUrl}
                alt={entry.location}
                style={{
                  width: '100%',
                  maxHeight: '300px',
                  objectFit: 'cover',
                  marginTop: '10px',
                  cursor: 'pointer'
                }}
                onClick={() => handleImageClick(entry.imageUrl)}  // Open image on click
              />
            </Paper>
          ))
        ) : (
          <Typography variant="body1">No memories for {selectedYear}.</Typography>
        )}
      </Box>

      {/* Fullscreen Image Modal */}
      <Dialog open={openImage} onClose={handleCloseImage} maxWidth="lg">
        <Box sx={{ position: 'relative' }}>
          {/* Close Button */}
          <IconButton
            onClick={handleCloseImage}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Full Image */}
          <img
            src={selectedImage}
            alt="Expanded view"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              maxHeight: '90vh',
            }}
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export default Timeline;
